import { Divider, Tab, TabGroup } from '@nike/eds'
import {
  useGetRasnFamilyGoodsholdersQuery,
  useGetRasnFamilyQuery,
  useGetRasnFamilyUnitsQuery,
  usePerformRasnFamilyActionMutation
} from 'api/rasn-family'
import { type Breadcrumb, Breadcrumbs } from 'components/breadcrumbs'
import { ConfirmationModal } from 'components/confirmation-modal'
import { CloseRasnIcon } from 'components/icon'
import { TableView, type TableColumn } from 'components/tableview'
import { ColumnType } from 'components/tableview/Filter'
import { CustomError } from 'pages/error-pages'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { showSnackbar } from 'redux/slices/globalState'
import { dispatch } from 'redux/store'
import {
  Division,
  GoodsholderStatus,
  RasnStatus,
  type RasnFamilyRasn,
  type RasnFamilyEvent,
  RasnFamilyStatus,
  type RasnGoodsholder,
  UnitStatus,
  getSimpleGrade,
  getSimpleGrades,
  type ErrorType
} from 'types'
import { getEnumValues } from 'utils/utils'

import './RasnFamilyDetails.scss'

export const RasnFamilyDetails = () => {
  const [tableColumns, setTableColumns] = useState<TableColumn[]>([])
  const [tableData, setTableData] = useState<any[]>([])
  const { rasnFamilyId } = useParams()
  const navigate = useNavigate()

  if (!rasnFamilyId) { throw new Error('RASN Family ID is required') }

  const [activeTab, setActiveTab] = useState('tab-rasns')
  const [performRasnFamilyAction] = usePerformRasnFamilyActionMutation()

  // Get data from API
  const { data: rasnFamilyDetails, isLoading, isError, error } = useGetRasnFamilyQuery(rasnFamilyId)
  const {
    data: rasnFamilyUnits,
    isLoading: isUnitsLoading,
    isError: isUnitsError,
    error: unitsError
  } = useGetRasnFamilyUnitsQuery(rasnFamilyId, {
    skip: activeTab !== 'tab-units'
  })
  const {
    data: rasnFamilyGoodsholders,
    isLoading: isGoodsholdersLoading,
    isError: isGoodsholdersError,
    error: goodsholdersError
  } = useGetRasnFamilyGoodsholdersQuery(rasnFamilyId, {
    skip: activeTab !== 'tab-goodsholders'
  })

  const rasnColumns = useMemo<TableColumn[]>(() => [
    { id: 'number', header: 'RASN Number', type: ColumnType.TEXT, columnWidth: 12 },
    { id: 'status', header: 'Status', type: ColumnType.ENUM, enumValues: getEnumValues(RasnStatus), columnWidth: 8 },
    { id: 'holdCode', header: 'Hold Code', type: ColumnType.TEXT, columnWidth: 10 },
    { id: 'expectedUnits', header: 'Expected', type: ColumnType.NUMBER, columnWidth: 8 },
    { id: 'handledUnits', header: 'Handled', type: ColumnType.NUMBER, columnWidth: 8 },
    { id: 'created', header: 'Created', type: ColumnType.DATE, columnWidth: 12 },
    { id: 'lastModified', header: 'Last Modified', type: ColumnType.DATE, columnWidth: 12 }
  ], [])

  const rasnFamilyUnitColumns = useMemo<TableColumn[]>(() => [
    { id: 'rasnNumber', header: 'RASN', type: ColumnType.TEXT, columnWidth: 10 },
    { id: 'sku', header: 'SKU', type: ColumnType.TEXT, columnWidth: 10 },
    { id: 'upc', header: 'UPC', type: ColumnType.TEXT, columnWidth: 9 },
    { id: 'division', header: 'Division', type: ColumnType.ENUM, enumValues: getEnumValues(Division), columnWidth: 6 },
    { id: 'silhouette', header: 'Silhouette', type: ColumnType.TEXT, columnWidth: 8 },
    { id: 'hashable', header: 'Hash', type: ColumnType.BOOLEAN, columnWidth: 3 },
    { id: 'quantity', header: 'Qty', type: ColumnType.NUMBER, columnWidth: 4 },
    { id: 'coo', header: 'CoO', type: ColumnType.TEXT, columnWidth: 4 },
    { id: 'simpleGrade', header: 'Grade', type: ColumnType.ENUM, enumValues: getSimpleGrades(), columnWidth: 4 },
    { id: 'gradeReason', header: 'Reason', type: ColumnType.TEXT, columnWidth: 10 },
    { id: 'gradeReasonCode', header: 'Reason Code', type: ColumnType.TEXT, columnWidth: 8 },
    { id: 'isegCode', header: 'ISEG', type: ColumnType.TEXT, columnWidth: 6 },
    { id: 'qualityCode', header: 'Quality', type: ColumnType.TEXT, columnWidth: 6 },
    { id: 'unitHandlingStation', header: 'Station', type: ColumnType.TEXT, columnWidth: 6 },
    { id: 'tote', header: 'Tote', type: ColumnType.TEXT, columnWidth: 11 },
    { id: 'receivedGoodsHolder', header: 'Goodsholder', type: ColumnType.TEXT, columnWidth: 11 },
    { id: 'status', header: 'Status', type: ColumnType.ENUM, enumValues: getEnumValues(UnitStatus), columnWidth: 6 },
    { id: 'modifiedBy', header: 'Athlete*', type: ColumnType.TEXT, columnWidth: 6 },
    { id: 'modifiedTimestamp', header: 'Modified', type: ColumnType.DATE, columnWidth: 12 }
  ], [])

  const eventColumns = useMemo<TableColumn[]>(() => [
    { id: 'type', header: 'Event Type', type: ColumnType.TEXT, columnWidth: 15 },
    { id: 'timestamp', header: 'Timestamp', type: ColumnType.DATE, columnWidth: 20 },
    { id: 'description', header: 'Description', type: ColumnType.TEXT, columnWidth: 40 }
  ], [])

  const materialsColumns = useMemo<TableColumn[]>(() => [
    { id: 'rasnNumber', header: 'RASN Number', type: ColumnType.TEXT, columnWidth: 15 },
    { id: 'sku', header: 'SKU', type: ColumnType.TEXT, columnWidth: 15 },
    { id: 'description', header: 'Description', type: ColumnType.TEXT, columnWidth: 25 },
    { id: 'expectedQuantity', header: 'Expected', type: ColumnType.NUMBER, columnWidth: 10 },
    { id: 'actualQuantity', header: 'Actual', type: ColumnType.NUMBER, columnWidth: 10 },
    { id: 'variance', header: 'Variance', type: ColumnType.NUMBER, columnWidth: 10 }
  ], [])

  const goodsholderColumns = useMemo<TableColumn[]>(() => [
    { id: 'lpn', header: 'Goodsholder LPN', type: ColumnType.TEXT, columnWidth: 16 },
    { id: 'actualQuantity', header: 'Handled', type: ColumnType.NUMBER, columnWidth: 8 },
    { id: 'status', header: 'Status', type: ColumnType.ENUM, enumValues: getEnumValues(GoodsholderStatus), columnWidth: 10 },
    { id: 'modifiedBy', header: 'Last modified by', type: ColumnType.TEXT, columnWidth: 12 },
    { id: 'modifiedTimestamp', header: 'Last modified time', type: ColumnType.DATE, columnWidth: 16 }
  ], [])

  // Generate materials data from RASNs - one row per material per RASN
  const materialsData = useMemo(() => {
    if (!rasnFamilyDetails) return []
    return rasnFamilyDetails.rasns.flatMap(rasn =>
      rasn.materials.map(material => ({
        rasnNumber: rasn.number,
        sku: material.sku,
        description: material.description,
        expectedQuantity: material.expectedQuantity,
        actualQuantity: material.actualQuantity,
        variance: material.actualQuantity - material.expectedQuantity
      }))
    )
  }, [rasnFamilyDetails])

  const groupedUnits = useMemo(() => {
    if (!rasnFamilyUnits) return []
    return rasnFamilyUnits.map(group => ({
      ...group,
      units: group.units.map(unit => ({
        ...unit,
        simpleGrade: getSimpleGrade(unit.grade)
      }))
    }))
  }, [rasnFamilyUnits])

  const flattenedUnits = useMemo(() => {
    return groupedUnits.flatMap(group =>
      group.units.map(unit => ({
        ...unit,
        rasnNumber: group.rasnNumber
      }))
    )
  }, [groupedUnits])

  const goodsholderRows = useMemo(() => rasnFamilyGoodsholders ?? [], [rasnFamilyGoodsholders])

  useEffect(() => {
    if (!rasnFamilyDetails) return
    let columns: TableColumn[] = []
    let data: any[] = []

    switch (activeTab) {
      case 'tab-rasns':
        columns = rasnColumns
        data = rasnFamilyDetails.rasns
        break
      case 'tab-materials':
        columns = materialsColumns
        data = materialsData
        break
      case 'tab-events':
        columns = eventColumns
        data = rasnFamilyDetails.events || []
        break
    }

    setTableColumns(columns)
    setTableData(data)
  }, [activeTab, rasnFamilyDetails, rasnColumns, materialsColumns, materialsData, eventColumns])

  if (isError) {
    const errorMessage = (error as ErrorType)?.data?.message || 'Failed to load RASN Family'
    return <CustomError error={errorMessage} />
  }

  if (isLoading || !rasnFamilyDetails) {
    return <div>Loading...</div>
  }

  const breadcrumbs: Breadcrumb[] = [
    { name: 'RASN Family', path: '/rasn-family' },
    { name: `${rasnFamilyDetails.id}` }
  ]

  return (
    <div className="m-4 rasn-family-details">
      <div className="header">
        <Breadcrumbs crumbs={breadcrumbs} />
        <div className="header-bar">
          <p><span className="label">Partner</span>{rasnFamilyDetails.partnerName || 'Unknown'}</p>
          <p><span className="label">Status</span>{rasnFamilyDetails.status || 'Unknown'}</p>
          <p><span className="label">Hold Code</span>{rasnFamilyDetails.holdCode || 'None'}</p>
          <p><span className="label">Total RASNs</span>{rasnFamilyDetails.totalRasns}</p>
          <p><span className="label">Expected</span>{rasnFamilyDetails.totalExpectedUnits}</p>
          <p><span className="label">Handled</span>{rasnFamilyDetails.totalHandledUnits}</p>
        </div>
        <div className="my-6 -mb-6 flex justify-end gap-4">
          {rasnFamilyDetails.holdCode && (
            <ConfirmationModal
              onTrigger={() => { dispatch(showSnackbar({ message: 'Unlocking RASN Families is not yet implemented.', status: 'info' })) }}
              title='Unlock RASN Family'
              message="Are you sure you want to unlock this RASN Family?"
              icon='Unlock'
              confirmText='Unlock'
            />
          )}
          {rasnFamilyDetails.status !== RasnFamilyStatus.CANCELLED && (
            <ConfirmationModal
              onTrigger={async () => {
                if (rasnFamilyDetails.status === RasnFamilyStatus.CLOSED) {
                  dispatch(showSnackbar({
                    message: `Family RSO ${rasnFamilyDetails.id} is already closed.`,
                    status: 'error'
                  }))
                  return
                }
                const result = await performRasnFamilyAction({ rasnFamilyId: rasnFamilyDetails.id, action: 'close' })
                if ('data' in result) {
                  dispatch(showSnackbar({
                    message: `All RASNs in the Family RSO ${rasnFamilyDetails.id} have been closed.`,
                    status: 'success'
                  }))
                }
              }}
              title='Close Family RSO'
              message="Are you sure you want to close this Family RSO? This will close all linked RASNs."
              icon={CloseRasnIcon}
              confirmText='Close'
            />
          )}
        </div>
      </div>
      <TabGroup
        className={'pt-6'}
        activeId={activeTab}
        name="tab-button-group"
        onChange={e => { setActiveTab(e.target.id) }}>
        <Tab id="tab-rasns">RASNs ({rasnFamilyDetails.rasns.length})</Tab>
        <Tab id="tab-materials">Materials</Tab>
        <Tab id="tab-units">Units</Tab>
        <Tab id="tab-goodsholders">GoodsHolders</Tab>
        <Tab id="tab-events">Events ({rasnFamilyDetails.events?.length || 0})</Tab>
      </TabGroup>
      <Divider className='bg-black p-px' />
      {activeTab === 'tab-units' && (
        <div className="pt-6">
          {isUnitsLoading && flattenedUnits.length === 0 && (
            <div>Loading units...</div>
          )}
          {isUnitsError && (
            <div className="eds-color--text-danger">
              {(unitsError as ErrorType)?.data?.message || 'Failed to load units for this RASN Family.'}
            </div>
          )}
          {!isUnitsLoading && !isUnitsError && flattenedUnits.length === 0 && (
            <div>No units found for this RASN Family.</div>
          )}
          {!isUnitsError && flattenedUnits.length > 0 && (
            <TableView
              columns={rasnFamilyUnitColumns}
              data={flattenedUnits}
              loading={isUnitsLoading}
            />
          )}
        </div>
      )}
      {activeTab === 'tab-goodsholders' && (
        <div className="pt-6">
          {isGoodsholdersLoading && goodsholderRows.length === 0 && (
            <div>Loading goodsholders...</div>
          )}
          {isGoodsholdersError && (
            <div className="eds-color--text-danger">
              {(goodsholdersError as ErrorType)?.data?.message || 'Failed to load goodsholders for this RASN Family.'}
            </div>
          )}
          {!isGoodsholdersLoading && !isGoodsholdersError && goodsholderRows.length === 0 && (
            <div>No goodsholders found for this RASN Family.</div>
          )}
          {!isGoodsholdersError && goodsholderRows.length > 0 && (
            <TableView
              columns={goodsholderColumns}
              data={goodsholderRows}
              loading={isGoodsholdersLoading}
              actions={[{
                label: 'View',
                icon: 'Show',
                hoverText: 'View Goodsholder details',
                onClick: (row: RasnGoodsholder) => { navigate(`/goodsholder/${row.lpn}`) }
              }]}
            />
          )}
        </div>
      )}
      {activeTab !== 'tab-units' && activeTab !== 'tab-goodsholders' && (
        <TableView
          columns={tableColumns}
          data={tableData}
          loading={false}
          actions={
            activeTab === 'tab-rasns'
              ? [{
                  label: 'View RASN',
                  icon: 'Show',
                  hoverText: 'View RASN details',
                  onClick: (row: RasnFamilyRasn) => { navigate(`/rasn/${row.number}`) }
                }]
              : activeTab === 'tab-events'
                ? [{
                    label: 'View',
                    icon: 'Show',
                    hoverText: 'View event details',
                    onClick: (row: RasnFamilyEvent) => { alert(`Event details: ${row.description}`) }
                    }]
                  : []
            }
          />
        )}
    </div>
  )
}
